import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedCart } from "@/utils/functions";
import { useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { FormattedDebts } from "@/utils/transform";
import { PropsView } from "@/pages/home/Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";
import { trackAddToCart } from "@/analytics/ecommerce";

export default function OrgPayment({
  id,
  dataTestId,
  disabled,
  textLabel,
  onClick,
  buttonType = "button",
  tracksAddToCart = false,
}: {
  id?: string;
  dataTestId?: string;
  disabled?: boolean;
  textLabel?: string;
  onClick?: () => void;
  buttonType?: "submit" | "button";
  tracksAddToCart?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const user = useSelector(
    (
      state: ReducersTypes & {
        userReducer: {
          debts: FormattedDebts[];
          cart: PropsView["selectables"];
          plate?: string;
        };
      },
    ) => state.userReducer,
  );

  const customer = useSelector(
    (
      state: ReducersTypes & {
        customerReducer: {
          client?: { id: string };
          selectedVehicles?: string[];
          vehicles?: { plate: string }[];
          debts?: FormattedDebts[];
          cart?: PropsView["selectables"];
        };
      },
    ) => state.customerReducer,
  );

  const isAuthenticated = customer?.client?.id;
  const currentCart = isAuthenticated ? customer.cart : user?.cart;
  const currentDebts = (isAuthenticated ? customer.debts : user?.debts) ?? [];

  let selectedPlate: string | string[] = "";

  if (isAuthenticated) {
    if (customer?.selectedVehicles?.length) {
      selectedPlate = customer.selectedVehicles;
    } else {
      selectedPlate = customer?.vehicles?.[0]?.plate ?? "";
    }
  } else {
    selectedPlate = user?.plate ?? "";
  }

  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => setCollapsed((collapsed) => !collapsed);

  const { total, filteredItems } = getFormattedCart({
    debts: currentDebts,
    cart: currentCart?.filter((item) => item.checked) ?? [],
  });

  const totalPassagens =
    filteredItems?.reduce(
      (sum, item) => sum + (item?.original?.valorPassagem ?? 0),
      0,
    ) ?? 0;
  const totalMultas =
    filteredItems?.reduce(
      (sum, item) => sum + (item?.original?.vlMulta ?? 0),
      0,
    ) ?? 0;

  const isClickable = total > 0 && !disabled && buttonType === "button";

  const handleClick = () => {
    if (!isClickable) return;

    if (tracksAddToCart) {
      trackAddToCart({
        flowType: isAuthenticated ? "pagamento_cadastrado" : "pagamento_avulso",
        value: total,
      });
    }

    if (onClick) {
      onClick();
      return;
    }
    navigate("/checkout");
  };

  const formatPlateLabel = (p: string) =>
    p.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1$2").toUpperCase();

  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 z-10 bg-white w-[calc(480px)] max-w-[calc(96%)] shadow-md rounded-t-theme-large p-3">
      <div
        className={`mb-1 transition-max-height duration-500 ease-in-out ${
          collapsed ? "max-h-[243px] overflow-auto" : "max-h-0 overflow-hidden"
        }`}
      >
        {isAuthenticated ? (
          <>
            {Array.isArray(selectedPlate) ? (
              selectedPlate.map((plate) => {
                const plateItems = filteredItems.filter(
                  (debt) => debt.plate === plate,
                );
                if (!plateItems.length) return null;
                return (
                  <div key={plate} className="mb-3">
                    <p className="text-base font-bold text-text uppercase mb-1">
                      {formatPlateLabel(plate)}
                    </p>
                    {plateItems.map((item, index) => (
                      <div key={index} className="mb-2">
                        <div className="flex w-full justify-between items-start gap-2">
                          <p className="text-sm text-text flex-1">
                            {item?.original?.fmtDataPassagem} -{" "}
                            {item?.original?.nmConcessionaria?.toUpperCase()}
                          </p>
                          <p className="text-sm text-text font-bold shrink-0">
                            R$ {item.price}
                          </p>
                        </div>
                        <p className="text-sm text-text">
                          {item?.original?.nmPraca}
                        </p>
                      </div>
                    ))}
                    <div className="w-full h-px bg-border mt-1 mb-1" />
                  </div>
                );
              })
            ) : selectedPlate ? (
              <div className="mb-3">
                <p className="text-base font-bold text-text uppercase mb-1">
                  {formatPlateLabel(selectedPlate)}
                </p>
                {filteredItems?.map((item, index) => (
                  <div key={index} className="mb-2">
                    <div className="flex w-full justify-between items-start gap-2">
                      <p className="text-sm text-text flex-1">
                        {item?.original?.fmtDataPassagem} -{" "}
                        {item?.original?.nmConcessionaria?.toUpperCase()}
                      </p>
                      <p className="text-sm text-text font-bold shrink-0">
                        R$ {item.price}
                      </p>
                    </div>
                    <p className="text-sm text-text">
                      {item?.original?.nmPraca}
                    </p>
                  </div>
                ))}
                <div className="w-full h-px bg-border mt-1 mb-1" />
              </div>
            ) : null}
            {filteredItems.length > 0 && (
              <div className="mb-2">
                <div className="flex w-full justify-between">
                  <p className="text-sm text-text">Passagens</p>
                  <p className="text-sm text-text">
                    R$ {totalPassagens.toFixed(2).replace(/\./g, ",")}
                  </p>
                </div>
                <div className="flex w-full justify-between">
                  <p className="text-sm text-text">Multas/Juros</p>
                  <p className="text-sm text-text">
                    R$ {totalMultas.toFixed(2).replace(/\./g, ",")}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : selectedPlate ? (
          <>
            <p className="text-lg mb-1 text-text font-bold">
              {t("common:plate")}:{" "}
              <span className="uppercase">
                {(selectedPlate as string).replace(
                  /^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/,
                  "$1-$2",
                )}
              </span>
            </p>
            {filteredItems?.map((item, index) => (
              <div key={index} className="mb-2">
                <div className="flex w-full justify-between">
                  <p className="text-sm text-text">
                    {item?.fmtDataPassagem ||
                      item?.original?.fmtDataPassagem ||
                      t("pages:home:dataUnavailable")}
                  </p>
                </div>
                <div className="flex w-full justify-between">
                  <p className="text-sm text-text">
                    {item?.original?.nmConcessionaria}
                  </p>
                </div>
                <div className="flex w-full justify-between">
                  <p
                    className={`text-sm text-text font-bold ${themeName === "zapay" ? "" : "blur-sm"}`}
                  >
                    {themeName === "zapay"
                      ? item?.original?.nmPraca
                      : t("pages:home:dataUnavailable")}
                  </p>
                  <p className="text-sm text-text font-bold">R$ {item.price}</p>
                </div>
              </div>
            ))}
          </>
        ) : null}
      </div>
      <div className="w-full flex justify-between items-center">
        <p className="text-lg text-text">{t("pages:home:totalPayable")}:</p>
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`duration-500 ease-in-out transform w-8 h-8 flex items-center justify-center ${total === 0 ? "opacity-50 cursor-not-allowed pointer-events-none" : "opacity-100 cursor-pointer"}`}
          style={{
            transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
            transition: "opacity 0.3s ease",
          }}
          disabled={total === 0}
        >
          <img
            src="/svg/icon-arrow-up.svg"
            width={24}
            height={24}
            alt={
              collapsed ? t("common:collapseTotal") : t("common:expandTotal")
            }
          />
        </button>
      </div>
      <div className="w-full h-px bg-border mt-2 mb-2" />
      <div className="w-full flex justify-between items-center">
        <p className="text-xl flex items-end text-text font-bold">
          <span className="text-base">R$</span>
          <b>
            &nbsp;
            {total ? total?.toFixed(2)?.toString().replace(/\./g, ",") : "0,00"}
          </b>
        </p>
        <button
          id={id}
          data-testid={dataTestId}
          type={buttonType}
          disabled={disabled || total === 0}
          className={`${disabled || total === 0 ? "bg-disabled text-disabled-text opacity-50 cursor-not-allowed" : "bg-primary text-white opacity-100 cursor-pointer"} px-6 py-2 rounded-theme-large font-bold transition-opacity duration-300`}
          onClick={handleClick}
        >
          {textLabel ?? t("common:continue")}
        </button>
      </div>
    </div>
  );
}
